import { Fragment, useState } from "react";
import type { MfgPricingLayerBand, MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import { isOverheadCogsColumn } from "@/lib/manufacturing/overheadCogsLink";
import { inputClass, mfgEmptyStateClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingPricingMaterialsCell } from "./ManufacturingPricingMaterialsCell";
import { MaterialsSourceHelp, PricingColumnSourceHelp } from "./PricingColumnSourceHelp";
import { PricingBandRoundUpSelect } from "./PricingBandRoundUpSelect";
import { formatUsd } from "./pricingGridFormat";
import { PricingBandTotalCell } from "./PricingBandTotalCell";
import type { ManufacturingPricingGridState } from "./useManufacturingPricingGrid";

type Props = {
  grid: ManufacturingPricingGridState;
  onOpenVariance?: (row: MfgPricingLayerGridRow) => void;
  alertThresholdPct?: number;
};

const BAND_CHROME: Record<
  MfgPricingLayerBand,
  { label: string; border: string; header: string; total: string }
> = {
  cogs: {
    label: "COGS",
    border: "border-red-400 dark:border-red-600",
    header: "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100",
    total: "bg-red-100/80 dark:bg-red-950/50",
  },
  wholesale: {
    label: "Wholesale",
    border: "border-amber-400 dark:border-amber-600",
    header: "bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100",
    total: "bg-amber-100/80 dark:bg-amber-950/50",
  },
  msrp: {
    label: "MSRP",
    border: "border-emerald-400 dark:border-emerald-600",
    header: "bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-100",
    total: "bg-emerald-100/80 dark:bg-emerald-950/50",
  },
};

function EditableCell({
  row,
  column,
  grid,
}: {
  row: MfgPricingLayerGridRow;
  column: MfgPricingLayerColumn;
  grid: ManufacturingPricingGridState;
}) {
  const stored = row.values_by_column_id[column.id];
  const [draft, setDraft] = useState(
    stored === null || stored === undefined ? "" : String(stored),
  );
  const [focused, setFocused] = useState(false);
  const fromRates =
    (column.key === "labor" || column.source_module === "labor") &&
    ((row.labor_rate_count ?? 0) > 0 || (row.labor_rate_skipped ?? 0) > 0);
  if (isOverheadCogsColumn(column)) {
    const amount = typeof stored === "number" && Number.isFinite(stored) ? stored : null;
    return (
      <div
        className="px-1 py-1 text-right"
        title={row.overhead_note || "Calculated on the Overhead screen."}
      >
        <div className="tabular-nums">{amount === null ? "—" : formatUsd(amount)}</div>
        {row.overhead_variance_label ? (
          <div className="text-[10px] font-normal leading-none text-gray-500 dark:text-zinc-400">
            {row.overhead_variance_label}
          </div>
        ) : null}
      </div>
    );
  }
  if (fromRates) {
    const skipped = row.labor_rate_skipped ?? 0;
    const priced = row.labor_rate_count ?? 0;
    const title = [
      priced > 0 ? `Sum of ${priced} labor rate${priced === 1 ? "" : "s"} matched to this SKU.` : "",
      skipped > 0
        ? `${skipped} matched rate${skipped === 1 ? "" : "s"} still need Pays by or a building size.`
        : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <div className="px-1 py-1 text-right" title={title}>
        <div className="tabular-nums">
          {priced > 0 ? formatUsd(row.labor_model_usd) : "—"}
        </div>
        <div className="text-[10px] font-normal leading-none text-gray-500 dark:text-zinc-400">
          {priced > 0 ? `${priced} rate${priced === 1 ? "" : "s"}` : null}
          {priced > 0 && skipped > 0 ? " · " : null}
          {skipped > 0 ? `${skipped} incomplete` : null}
        </div>
      </div>
    );
  }

  const display = focused ? draft : stored === null || stored === undefined ? "" : String(stored);

  return (
    <input
      className={`${inputClass} min-w-[5.5rem] px-1 py-1 text-right text-xs tabular-nums`}
      value={display}
      disabled={grid.actionBusy}
      onFocus={() => {
        setFocused(true);
        setDraft(stored === null || stored === undefined ? "" : String(stored));
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const next = draft.trim();
        const prev = stored === null || stored === undefined ? "" : String(stored);
        if (next === prev) return;
        if (!next) return;
        void grid.saveCell(row, column.id, next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      aria-label={`${column.label} for ${row.name}`}
    />
  );
}

export function ManufacturingPricingGridTable({ grid, onOpenVariance, alertThresholdPct }: Props) {
  const g = grid;
  if (g.listBusy && g.rows.length === 0) {
    return <div className={mfgEmptyStateClass}>Loading COGS…</div>;
  }
  if (!g.listBusy && g.rows.length === 0) {
    return (
      <div className={mfgEmptyStateClass}>
        {g.filters.sku_scope === "addons"
          ? "No options or packages match these filters."
          : "No product recipes match these filters. Create kind='product' recipes under Models (BOM); they appear here for org-wide or plant-scoped COGS even without a build-model shell."}
      </div>
    );
  }

  const bands: MfgPricingLayerBand[] = ["cogs", "wholesale", "msrp"];

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-zinc-800">
      <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-zinc-800">
        <thead>
          <tr className="bg-gray-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-zinc-900 dark:text-zinc-400">
            <th className="sticky left-0 z-20 bg-gray-50 px-2 py-2 dark:bg-zinc-900" rowSpan={2}>
              <input
                type="checkbox"
                checked={g.allVisibleSelected}
                onChange={() => g.toggleSelectAllVisible()}
                aria-label="Select all visible rows"
              />
            </th>
            <th className="sticky left-8 z-20 bg-gray-50 px-2 py-2 dark:bg-zinc-900" rowSpan={2}>
              Item
            </th>
            <th className="px-2 py-2" rowSpan={2}>
              Kind
            </th>
            <th className="px-2 py-2 text-right" rowSpan={2}>
              <span className="inline-flex items-center justify-end gap-1">
                Materials
                <MaterialsSourceHelp />
              </span>
            </th>
            {bands.map((band) => {
              const cols = g.columnsByBand[band];
              const chrome = BAND_CHROME[band];
              return (
                <th
                  key={band}
                  colSpan={cols.length + 1}
                  className={`border-x-2 px-2 py-2 text-center ${chrome.border} ${chrome.header}`}
                >
                  {chrome.label}
                </th>
              );
            })}
          </tr>
          <tr className="bg-gray-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-zinc-900 dark:text-zinc-400">
            {bands.map((band) => {
              const cols = g.columnsByBand[band];
              const chrome = BAND_CHROME[band];
              return (
                <Fragment key={`${band}-sub`}>
                  {cols.map((c) => (
                    <th
                      key={c.id}
                      className={`border-x px-2 py-2 text-right ${chrome.border} ${chrome.header}`}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        {c.label}
                        <PricingColumnSourceHelp column={c} placement={band === "msrp" ? "left" : "right"} />
                      </span>
                    </th>
                  ))}
                  <th className={`border-x-2 px-2 py-2 text-right ${chrome.border} ${chrome.total}`}>
                    <span className="inline-flex flex-col items-end gap-1">
                      <span>{BAND_CHROME[band].label} Σ</span>
                      {band === "cogs" ? null : (
                        <PricingBandRoundUpSelect
                          band={band}
                          columns={g.columns}
                          rows={g.catalogRows}
                          busy={g.actionBusy || g.listBusy}
                          onColumns={g.setColumns}
                          onRows={g.setCatalogRows}
                          onError={g.reportError}
                          onReload={g.reloadGrid}
                        />
                      )}
                    </span>
                  </th>
                </Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white dark:divide-zinc-900 dark:bg-zinc-950">
          {g.rows.map((row) => {
            const key = g.rowKey(row);
            const selected = g.selectedKeys.has(key);
            return (
              <tr key={key} className={selected ? "bg-indigo-50/60 dark:bg-indigo-950/20" : undefined}>
                <td className="sticky left-0 z-10 bg-inherit px-2 py-2">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => g.toggleRow(row)}
                    aria-label={`Select ${row.name}`}
                  />
                </td>
                <td className="sticky left-8 z-10 max-w-[14rem] truncate bg-inherit px-2 py-2 font-medium">
                  <div className="truncate">{row.name}</div>
                  <div className="truncate text-[10px] font-normal text-gray-500 dark:text-zinc-400">
                    {row.code}
                  </div>
                </td>
                <td className="px-2 py-2 text-gray-600 dark:text-zinc-400">
                  {row.subject_kind === "recipe" ? row.recipe_kind || "product" : row.subject_kind}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  <ManufacturingPricingMaterialsCell row={row} />
                </td>
                {bands.map((band) => {
                  const cols = g.columnsByBand[band];
                  const chrome = BAND_CHROME[band];
                  return (
                    <Fragment key={`${key}-${band}`}>
                      {cols.map((c) => (
                        <td key={c.id} className={`border-x px-1 py-1 ${chrome.border}`}>
                          <EditableCell row={row} column={c} grid={g} />
                        </td>
                      ))}
                      <td
                        className={`border-x-2 px-2 py-1 text-right font-semibold tabular-nums ${chrome.border} ${chrome.total}`}
                      >
                        <PricingBandTotalCell
                          band={band}
                          row={row}
                          alertThresholdPct={alertThresholdPct}
                          onOpen={band === "cogs" || !onOpenVariance ? undefined : () => onOpenVariance(row)}
                        />
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
